/**
 * Dashboard Service
 *
 * Wraps the role-tier-aware /dashboard/metrics endpoint that powers
 * the three Dashboard surfaces (super_admin, manager, tech).
 *
 * The server scopes the response by role; the client only chooses
 * the period + (for managers) the optional `scope` filter that
 * narrows revenue between "me" / "company" / "all" within their
 * accessible companies.
 *
 * Backend endpoint: GET /api/v1/dashboard/metrics
 */

import { api } from './api';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** The three tiers the response can be shaped into. */
export type DashboardTier = 'super_admin' | 'manager' | 'tech';

/** A single ISO date pair describing a period. */
export interface DashboardPeriod {
    from: string;
    to: string;
}

/** Comparison window — same shape as DashboardPeriod plus a label. */
export interface DashboardComparisonPeriod extends DashboardPeriod {
    label: string;
}

/** Per-status count map (e.g. { pending: 4, in_progress: 2, completed: 7 }) */
export type StatusCountMap = Record<string, number>;

/** Counts for one record type. */
export interface DashboardRecordCounts {
    total: number;
    byStatus: StatusCountMap;
}

/**
 * Inspection counts, plus the three-tier rating rollup.
 *
 * byRating always carries every tier — good / needs_attention / critical —
 * plus `unrated` for inspections nobody has scored yet, so the dashboard
 * renders a stable set of tiles rather than a shifting one.
 */
export interface DashboardInspectionCounts extends DashboardRecordCounts {
    byRating: Record<string, number>;
    /** Open inspections carrying at least one unresolved critical finding. */
    openCritical: number;
}

/** Combined counts block returned for every tier. */
export interface DashboardCounts {
    workOrders: DashboardRecordCounts;
    inspections: DashboardInspectionCounts;
    completedCurrent: number;
    completedPrevious: number;
    completedDelta: number;
}

/** Revenue block — present for super_admin + manager tiers, absent for tech. */
export interface DashboardRevenue {
    current: number;
    previous: number;
    delta: number;
    estimateApprovedTotal: number;
    estimateApprovedCount: number;
}

/** Per-user task aggregate row (super_admin tier only). */
export interface DashboardUserTaskRow {
    userId: number;
    name: string;
    open: number;
    completed: number;
}

/** Compact event row used in tech-tier upcoming/recently-completed lists. */
export interface DashboardEventRow {
    id: string;
    title: string;
    /** ISO date — present on upcoming items as `date`, on completed as `completedAt`. */
    date?: string;
    completedAt?: string;
    status?: string;
    companyName: string | null;
    locationName?: string | null;
    detailUrl: string;
}

/** Tech-tier upcoming + completed lists. */
export interface DashboardLists {
    workOrders: DashboardEventRow[];
    inspections: DashboardEventRow[];
}

/**
 * The full metrics payload. Optional fields reflect tier-specific
 * presence: revenue is undefined for tech, tasksByUser is undefined
 * for non-super, upcoming/recentlyCompleted are undefined for
 * non-tech.
 */
export interface DashboardMetrics {
    tier: DashboardTier;
    period: DashboardPeriod;
    comparison: DashboardComparisonPeriod | null;
    counts: DashboardCounts;
    revenue?: DashboardRevenue;
    tasksByUser?: DashboardUserTaskRow[];
    upcoming?: DashboardLists;
    recentlyCompleted?: DashboardLists;
}

/**
 * Filters accepted by GET /dashboard/metrics. All optional — the
 * server applies sensible defaults (current month + previous month
 * comparison + 'company' scope).
 */
export interface DashboardFilters {
    from?: string;
    to?: string;
    compareFrom?: string;
    compareTo?: string;
    /** Manager-tier scope: 'me' / 'company' / 'all'. 'all' caps to
     *  'company' for non-super users on the server. */
    scope?: 'me' | 'company' | 'all';
    /** Optional company filter (super-admin or company-tier manager). */
    companyId?: number;
}

/* ============================================
 * SERVICE
 * ============================================ */

/** Convert camelCased filter keys to the snake_case query string the server expects. */
function buildParams(filters: DashboardFilters): Record<string, string> {
    const params: Record<string, string> = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.compareFrom) params.compare_from = filters.compareFrom;
    if (filters.compareTo) params.compare_to = filters.compareTo;
    if (filters.scope) params.scope = filters.scope;
    if (filters.companyId != null) params.company_id = String(filters.companyId);
    return params;
}

export const dashboardService = {
    /** GET /dashboard/metrics — returns the tier-shaped payload. */
    metrics: (filters: DashboardFilters = {}) =>
        api.get<{ data: DashboardMetrics }>('/dashboard/metrics', buildParams(filters)),
};
